import { Box, Stack, Typography } from '@mui/material';
import { useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ButtonTypeEnum } from '../../enums/button-type-enum';
import { SnackbarSeverityEnum } from '../../enums/snackbar-severity-enum';
import {
  IWorkExperience,
  GetWorkExperienceRes,
} from '../../interfaces/user-work-experience.interface';
import SectionLayout from '../../layouts/SectionLayout';
import {
  useCreateWorkExperienceMutation,
  useDeleteWorkExperienceByIdMutation,
  useUpdateWorkExperienceByIdMutation,
} from '../../store/apiSlice/workExperienceApi';
import {
  errorHelper,
  promiseAllErrorsToString,
} from '../../utils/helper/error-helper';
import WorkExperienceForm, {
  FormRef,
  WorkExperienceFormInputs,
} from '../Auth/VerifyAccount/WorkExperience/components/WorkExperienceForm';
import CustomButton from '../MUIComponents/CustomButton';
import SnackbarCustom from '../MUIComponents/SnackbarCustom';
import EmptyWorkExperience from '../../pages/home/PersonalInfoPage/tabs/WorkExperienceTab/components/EmptyWorkExperience';
import useSnackbar from '../../hooks/useSnackBar';
import userApi from '../../store/apiSlice/userApi';

interface WorkExperiencesSectionProps {
  workExperiences?: GetWorkExperienceRes[];
}

const WorkExperiencesSection = ({
  workExperiences,
}: WorkExperiencesSectionProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmptyFormVisible, setIsEmptyFormVisible] = useState(false);
  const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
  const formRef = useRef<FormRef | null>(null);
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const [createWorkExperience] = useCreateWorkExperienceMutation();
  const [updateWorkExperience] = useUpdateWorkExperienceByIdMutation();
  const [deleteWorkExperience] = useDeleteWorkExperienceByIdMutation();

  const sortedWorkExps = useMemo(() => {
    if (!workExperiences) {
      return [];
    }

    return [...workExperiences].sort(
      (a, b) =>
        new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );
  }, [workExperiences]);

  const handleWorkExperienceDeletion = async (
    workExpsToDelete: IWorkExperience[]
  ) => {
    if (!workExpsToDelete.length) return;

    const deleteRes = await Promise.all(
      workExpsToDelete.map((we) => deleteWorkExperience(we.id ?? ''))
    );
    const errorMessage = promiseAllErrorsToString(deleteRes);

    if (errorMessage) {
      throw new Error(errorMessage);
    }
  };

  const handleWorkExperienceUpdates = async (
    updatedWorkExperiences: IWorkExperience[]
  ) => {
    if (!updatedWorkExperiences.length) return;

    const updateRes = await Promise.all(
      updatedWorkExperiences.map(updateWorkExperience)
    );
    const errorMessage = promiseAllErrorsToString(updateRes);

    if (errorMessage) {
      throw new Error(errorMessage);
    }
  };

  const handleWorkExperienceCreation = async (
    workExperiencesToCreate: IWorkExperience[]
  ) => {
    if (!workExperiencesToCreate.length) return;

    const createRes = await Promise.all(
      workExperiencesToCreate.map(createWorkExperience)
    );
    const errorMessage = promiseAllErrorsToString(createRes);

    if (errorMessage) {
      throw new Error(errorMessage);
    }
  };

  const handleSubmissionError = (error: unknown) => {
    const errorMessage = errorHelper(error);
    showSnackbar(errorMessage, SnackbarSeverityEnum.ERROR);
  };

  const onSave = async () => {
    const handleValidation = formRef?.current?.handleSubmit;

    if (!handleValidation) {
      return;
    }

    const executeSubmission = handleValidation(
      async (data: WorkExperienceFormInputs) => {
        const formDataWorkExperiences = data.workExperiences;
        setIsSubmitting(true);

        try {
          const workExperienceToUpdate: IWorkExperience[] = [];
          const workExperienceToCreate: IWorkExperience[] = [];

          formDataWorkExperiences.forEach((uwe: IWorkExperience) =>
            uwe.id
              ? workExperienceToUpdate.push(uwe)
              : workExperienceToCreate.push(uwe)
          );
          const workExpsToDelete = sortedWorkExps.filter(
            (we) => !workExperienceToUpdate.some((uwe) => uwe.id === we.id)
          );

          await handleWorkExperienceDeletion(workExpsToDelete);
          await handleWorkExperienceUpdates(workExperienceToUpdate);
          await handleWorkExperienceCreation(workExperienceToCreate);

          // Ensure unified /me data is refreshed after work experience updates
          // on the /profile settings page.
          dispatch(userApi.util.invalidateTags(['user']));

          // formRef?.current?.reset(data, { keepDefaultValues: false });

          showSnackbar(
            'Work experience successfully updated.',
            SnackbarSeverityEnum.SUCCESS
          );
        } catch (error) {
          handleSubmissionError(error);
        } finally {
          setIsSubmitting(false);
        }
      }
      // (errors) => console.log('Validation Errors:', errors)
    );

    await executeSubmission();
  };

  const onCancel = () => {
    formRef.current?.reset();
  };

  return (
    <SectionLayout>
      <Typography component="h2" variant="h2" mb={6}>
        Work experience{workExperiences && workExperiences?.length > 1 && 's'}
      </Typography>

      <Box display="flex" flexDirection="column" rowGap="20px">
        {(!!workExperiences?.length || isEmptyFormVisible) && (
          <WorkExperienceForm
            ref={formRef}
            savedWorkExperiences={sortedWorkExps}
            setIsFormChanged={setIsFormChanged}
          />
        )}

        {!workExperiences?.length && !isEmptyFormVisible && (
          <EmptyWorkExperience
            isOwnPage
            handleAddWorkExperience={() => setIsEmptyFormVisible(true)}
            sx={{ minHeight: { xs: 'auto', md: '300px' }, gap: '2rem' }}
          />
        )}
      </Box>

      {(!!workExperiences?.length || isEmptyFormVisible) && (
        <Stack direction="row" columnGap="16px" mt={6}>
          <CustomButton
            onClick={onSave}
            variant="outlined"
            buttonWidth={220}
            disabled={isSubmitting || !isFormChanged}
          >
            save
          </CustomButton>

          <CustomButton
            onClick={onCancel}
            variant="outlined"
            buttonWidth={220}
            variantType={ButtonTypeEnum.SECONDARY}
            disabled={isSubmitting || !isFormChanged}
          >
            cancel
          </CustomButton>
        </Stack>
      )}

      <SnackbarCustom
        open={snackbar.open}
        onClose={closeSnackbar}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </SectionLayout>
  );
};

export default WorkExperiencesSection;
